import { lazy, Suspense } from 'react';
import ReactDOM from 'react-dom/client';
import App from './App.tsx';
import './index.css';
import { NotificationProvider } from './contexts/NotificationContext';
import { SessionProvider } from './contexts/SessionContext';
import { ErrorBoundary } from './components/ErrorBoundary';
import { apiClient } from './api/client';
import { sweepStaleWizardDrafts } from './utils/wizardDraft';
import { startUpdateChecks } from './utils/updateCheck';
import { UpdateBanner } from './components/UpdateBanner';
import { reloadPage, restoreScroll } from './utils/reloadState';

// Catch errors outside React's render (event handlers, timers, async code)
// that the ErrorBoundary below can't see, and report them the same way.
window.addEventListener('error', (event) => {
  apiClient.errors.report({
    message: event.message,
    stack: event.error?.stack,
    url: window.location.href,
  });
});

window.addEventListener('unhandledrejection', (event) => {
  const reason = event.reason;
  apiClient.errors.report({
    message: reason?.message ? String(reason.message) : String(reason),
    stack: reason?.stack,
    url: window.location.href,
  });
});

// Wizard drafts nobody can resume any more — they otherwise stay for good and, each
// holding a roster PDF, fill the storage the device's own settings live in.
sweepStaleWizardDrafts();

// Lazily-loaded chunks (wizard, chops) are fetched by hashed filename. A tab
// left open across a redeploy asks for a hash that no longer exists — reload
// once onto the new build instead of leaving the feature broken.
// Guarded so a chunk that is genuinely missing can't cause a reload loop.
window.addEventListener('vite:preloadError', (event) => {
  try {
    const last = Number(sessionStorage.getItem('chunkReloadAt') || 0);
    if (Date.now() - last < 10_000) return;
    sessionStorage.setItem('chunkReloadAt', String(Date.now()));
  } catch { /* storage unavailable — still try the one reload */ }
  event.preventDefault();
  reloadPage();
});

// Force a full reload when the browser restores this page from bfcache.
// React apps with WebSockets are frozen in bfcache and can appear blank
// when restored; a fresh load reinitializes React and reconnects sockets.
window.addEventListener('pageshow', (event) => {
  if (event.persisted) {
    reloadPage();
  }
});

startUpdateChecks();

// Two workspaces share this one app and URL: /chops (chops fill the HO/TO form,
// no ORCA session needed) and everything else — /car included — is ORCA itself.
// /chops mounts without SessionProvider on purpose: a session joined in this tab
// would otherwise put its X-Session-Id on every chops request.
const isChops = /^\/chops?\/?$/.test(window.location.pathname);   // /chop too: it is easy to type

// Chops is a separate workspace — its code (and the html2canvas it uses) is only
// fetched on /chops, never on an ORCA page load.
const ChopsApp = lazy(() => import('./components/chops/ChopsApp').then((m) => ({ default: m.ChopsApp })));

// StrictMode intentionally omitted. Re-enabling it double-invokes effects in
// development, which would need the WebSocket connect/disconnect and canvas
// render effects audited for double-run safety first — a deliberate change,
// not something to flip on by default.
ReactDOM.createRoot(document.getElementById('root')!).render(
  isChops ? (
    <NotificationProvider>
      <ErrorBoundary>
        <Suspense fallback={null}>
          <ChopsApp />
        </Suspense>
      </ErrorBoundary>
    </NotificationProvider>
  ) : (
    <SessionProvider>
      <NotificationProvider>
        <ErrorBoundary>
          <App />
          <UpdateBanner />
        </ErrorBoundary>
      </NotificationProvider>
    </SessionProvider>
  )
);

// Put scrolling back after a self-reload (retries while the roster loads in).
restoreScroll();
